import { Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useIntl } from 'react-intl';
import { TProductInformation } from 'src/stores/types/ProductTypes';

interface ComposedProductAvailabilityLabelProps {
    bikeInfo: TProductInformation;
    batteryInfo: TProductInformation | null;
    saddleAndGripInfo: TProductInformation | null;
    strapInfo: TProductInformation | null;
    frameInfo?: TProductInformation | null;
    carrierInfo?: TProductInformation | null;
}

const ComposedProductAvailabilityLabel = ({
    bikeInfo,
    batteryInfo,
    saddleAndGripInfo,
    strapInfo,
    frameInfo,
    carrierInfo,
}: ComposedProductAvailabilityLabelProps) => {
    const intl = useIntl();

    let bikeAvailabilityDate = intl.formatMessage({
        id: bikeInfo.inStock
            ? 'status.inStock'
            : bikeInfo.availableFrom == null
            ? 'status.Unavailable'
            : 'status.outStock',
    });

    let batteryAvailabilityDate = null;
    if (batteryInfo) {
        batteryAvailabilityDate = intl.formatMessage({
            id: batteryInfo!.inStock
                ? 'status.inStock'
                : batteryInfo!.availableFrom == null
                ? 'status.Unavailable'
                : 'status.outStock',
        });
    }

    let saddleAndGripAvailabilityDate = null;
    if (saddleAndGripInfo) {
        saddleAndGripAvailabilityDate = intl.formatMessage({
            id: saddleAndGripInfo.inStock
                ? 'status.inStock'
                : saddleAndGripInfo.availableFrom == null
                ? 'status.Unavailable'
                : 'status.outStock',
        });
    }

    let strapAvailabilityDate = null;
    if (strapInfo) {
        strapAvailabilityDate = intl.formatMessage({
            id: strapInfo.inStock
                ? 'status.inStock'
                : strapInfo.availableFrom == null
                ? 'status.Unavailable'
                : 'status.outStock',
        });
    }

    let frameAvailabilityDate = null;
    if (frameInfo) {
        frameAvailabilityDate = intl.formatMessage({
            id: frameInfo.inStock
                ? 'status.inStock'
                : frameInfo.availableFrom == null
                ? 'status.Unavailable'
                : 'status.outStock',
        });
    }

    let carrierAvailabilityDate = null;
    if (carrierInfo) {
        carrierAvailabilityDate = intl.formatMessage({
            id: carrierInfo.inStock
                ? 'status.inStock'
                : carrierInfo.availableFrom == null
                ? 'status.Unavailable'
                : 'status.outStock',
        });
    }

    let deliveryDate = null;
    if (!bikeInfo.inStock && bikeInfo.availableFrom !== null) {
        deliveryDate = dayjs(bikeInfo.availableFrom || '');
    }
    if (batteryInfo && !batteryInfo.inStock && batteryInfo.availableFrom !== null) {
        if (!deliveryDate) {
            deliveryDate = dayjs(batteryInfo.availableFrom || '');
        } else if (dayjs(batteryInfo.availableFrom || '').isAfter(deliveryDate)) {
            deliveryDate = dayjs(batteryInfo.availableFrom || '');
        }
    }
    if (saddleAndGripInfo && !saddleAndGripInfo.inStock && saddleAndGripInfo.availableFrom !== null) {
        if (!deliveryDate) {
            deliveryDate = dayjs(saddleAndGripInfo.availableFrom || '');
        } else if (dayjs(saddleAndGripInfo.availableFrom || '').isAfter(deliveryDate)) {
            deliveryDate = dayjs(saddleAndGripInfo.availableFrom || '');
        }
    }
    if (strapInfo && !strapInfo.inStock && strapInfo.availableFrom !== null) {
        if (!deliveryDate) {
            deliveryDate = dayjs(strapInfo.availableFrom || '');
        } else if (dayjs(strapInfo.availableFrom || '').isAfter(deliveryDate)) {
            deliveryDate = dayjs(strapInfo.availableFrom || '');
        }
    }
    if (frameInfo && !frameInfo.inStock && frameInfo.availableFrom !== null) {
        if (!deliveryDate) {
            deliveryDate = dayjs(frameInfo.availableFrom || '');
        } else if (dayjs(frameInfo.availableFrom || '').isAfter(deliveryDate)) {
            deliveryDate = dayjs(frameInfo.availableFrom || '');
        }
    }
    if (carrierInfo && !carrierInfo.inStock && carrierInfo.availableFrom !== null) {
        if (!deliveryDate) {
            deliveryDate = dayjs(carrierInfo.availableFrom || '');
        } else if (dayjs(carrierInfo.availableFrom || '').isAfter(deliveryDate)) {
            deliveryDate = dayjs(carrierInfo.availableFrom || '');
        }
    }

    return (
        <>
            <Typography
                style={{
                    backgroundColor: '#e0e0e0',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    fontSize: '0.8125rem',
                }}
                data-testid="product-availability-badge"
                variant="subtitle2"
                gutterBottom
            >
                {`${intl.formatMessage({ id: 'returns.bike' })}: ${bikeAvailabilityDate}`}
                {batteryInfo && (
                    <>
                        <br />
                        {`${intl.formatMessage({ id: 'returns.typeBattery' })}: ${batteryAvailabilityDate}`}
                    </>
                )}
                {saddleAndGripInfo && (
                    <>
                        <br />
                        {`${intl.formatMessage({ id: 'cart.saddle_and_grip' })}: ${saddleAndGripAvailabilityDate}`}
                    </>
                )}
                {strapInfo && (
                    <>
                        <br />
                        {`${intl.formatMessage({ id: 'cart.strap' })}: ${strapAvailabilityDate}`}
                    </>
                )}
                {frameInfo && (
                    <>
                        <br />
                        {`${intl.formatMessage({ id: 'cart.frame.color' })}: ${frameAvailabilityDate}`}
                    </>
                )}
                {carrierInfo && (
                    <>
                        <br />
                        {`${intl.formatMessage({ id: 'cart.carrier' })}: ${carrierAvailabilityDate}`}
                    </>
                )}
            </Typography>
            {deliveryDate && (
                <Chip
                    data-testid="product-availability-date-badge"
                    style={{ lineHeight: 'inherit' }}
                    label={`${intl.formatMessage({
                        id: 'status.expectedAvailabilityFrom',
                    })} ${deliveryDate.format('DD/MM/YYYY')}`}
                />
            )}
        </>
    );
};

export default ComposedProductAvailabilityLabel;
